import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { stageAPI } from '../services/api';
import ChampDate from './ChampDate';
import StageModale from './StageModale';
import { MenuFormations, autresProcessus, nomPropose, dansLOrdre, formationsProposees, estApprentissage, REGIMES } from './StageFormationsProcessus';

/**
 * SUIVI DE STAGE — Paramétrage › Configurations (lot 3.0)
 * =======================================================
 * Spec     : doc/modules/suivi_de_stage.md §6 « Configurations »
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#psc-config)
 *
 * ── DIX ÉTAPES, PAS SEPT (écart E1) ─────────────────────────────────────────
 * Le §6 de la spec décrit un éditeur à sept étapes et le test S15 parle de
 * « 7 curseurs ». Les deux datent d'avant le passage du workflow à dix étapes.
 * La maquette est à jour, la prose ne l'est pas. La liste des étapes n'est même
 * pas écrite dans ce fichier : elle vient du SERVEUR (`donnees.etapes`), qui la
 * tient de `stageParametrageService`. Une liste recopiée à l'écran finit par ne
 * plus correspondre à celle que le serveur applique, et c'est le serveur qui a
 * le dernier mot.
 *
 * ── L'ÉDITEUR EST UNE LIGNE DU TABLEAU ──────────────────────────────────────
 * Il s'insère SOUS la ligne cliquée (S29), se referme au reclic (S30), et se
 * déplace si on clique une autre ligne (S31). Pour une création, il s'ajoute EN
 * BAS (S36). D'où un état unique — `ouvert` — qui vaut un identifiant, la chaîne
 * `'new'`, ou `null` : deux états séparés (« ouvert » et « lequel ») auraient pu
 * se contredire.
 *
 * ── CE QUI N'EST PAS DANS CET ÉCRAN, ET POURQUOI ────────────────────────────
 * ⚠️ Les étapes obligatoires ont leur bascule grisée. Ce n'est PAS ce qui les
 * protège : le serveur les force à `true` quoi qu'on lui envoie
 * (`normaliserEtapesConfig`). Un écran est une commodité, pas une protection —
 * et le banc d'auto-contrôle du lot le vérifie en envoyant exprès `actif: false`
 * sur les six.
 */

/** Les dix numéros en chiffres cerclés, comme la maquette les dessine. */
const NUMEROS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '⑪'];

/** Icônes par étape — reprises une à une de la maquette (règle 4). */
const ICONES_ETAPE = {
    etape1:  'fa-paper-plane',      etape2:  'fa-inbox',
    // E173 (v85) : ③ la validation du responsable (maquette rév. 8)
    etape_resp: 'fa-user-check',
    etape3:  'fa-share',            etape4:  'fa-circle-check',
    etape5:  'fa-stamp',            etape6:  'fa-file-signature',
    etape7:  'fa-file-arrow-up',    etape8:  'fa-eye',
    etape9:  'fa-star-half-stroke', etape10: 'fa-flag-checkered'
};

/** Sous-titres de la maquette, sur les étapes qui en portent un. */
const SOUS_TITRES = {
    etape2:  'Étape automatique',
    // E173 (v85, maquette rév. 8)
    etape_resp: "Le responsable des stages relit la fiche de l'étudiant avant qu'elle parte au maître · désactivée : la fiche part directement à ④",
    etape4:  "Étape automatique · si le maître modifie ce que l'étudiant avait saisi : notification et courriel aux responsables des stages",
    etape5:  "Assistante · l'alerte « modifiée par le maître » informe, elle ne bloque pas",
    etape8:  'Désactiver → N/A dans la progression',
    etape10: 'Automatique quand ⑨ + ⑩ complétés · BUT1 : dès ⑩'
};

/**
 * Glyphes des modes de convention.
 * ⚠️ Écart de charte (règle 6) : la maquette écrit 🏛️ / 📮 / 📧. Les emoji ne
 * passent pas dans l'interface livrée ; ce sont des glyphes FontAwesome.
 */
const ICONES_MODE = { retrait: 'fa-building-columns', postal: 'fa-envelopes-bulk', numerique: 'fa-envelope-open-text' };

const jjmm = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** `YYYY-MM-DD` pour un champ de date (ChampDate), sans décalage de fuseau. */
const pourInputDate = (iso) => (iso ? String(iso).slice(0, 10) : '');

/**
 * Lot L1a (E169) — `seulement` : l'identifiant du processus de la VIGNETTE ouverte. Le tableau ne montre que lui,
 * son éditeur est ouvert d'office (et se rouvre après un enregistrement : dans une vignette, « Le processus » EST
 * l'éditeur), « Récupérer N-1 » et « Nouveau processus » passent sur l'accueil. `onEcrit` : après une écriture
 * (l'accueil recalcule ses compteurs et « ce qui reste à faire ») ; `onSupprime` : le processus n'existe plus.
 */

/**
 * L2a (E170, maquette rév. 10) : dans la vignette d'un processus, ses modèles sont SES copies — ils se lisent ici et se
 * changent dans l'onglet « Les documents » (Modifier, Repartir d'un autre modèle).
 */
function ModeleDuProcessus({ nom }) {
    return (
        <span className="cfg-modele-processus" style={{ flex: 1, fontSize: 12 }}>
            {nom ? <span className="pf-chip ok">{nom}</span> : <span className="pf-chip">aucun modèle</span>}
            <span style={{ color: '#64748b', fontSize: 11 }}> — se modifie dans l'onglet <b>② Les documents</b></span>
        </span>
    );
}

function StageParametrageConfigurations({ donnees, departementId, anneeId, modifiable, onMiseAJour, seulement = null, onEcrit = null, onSupprime = null, manquants = [] }) {
    const [ouvert, setOuvert]         = useState(null);   // id · 'new' · null
    const [brouillon, setBrouillon]   = useState(null);
    const [enCours, setEnCours]       = useState(false);
    const [erreur, setErreur]         = useState(null);
    const [aSupprimer, setASupprimer] = useState(null);
    const [bilan, setBilan]           = useState(null);   // modale de confirmation APRÈS l'acte
    const [confirmeN1, setConfirmeN1] = useState(false);

    const configurations = donnees.configurations || [];
    const etapes         = donnees.etapes || [];
    const modesRef       = donnees.modes_convention || [];
    const modeles        = donnees.modeles || {};

    /**
     * Les formations proposées à la CRÉATION : seules celles qui ont des étudiants
     * FI. La spec en fait un prérequis bloquant, et proposer une formation vide
     * reviendrait à laisser créer une configuration sans un seul dossier — panne
     * que l'utilisateur chercherait ensuite du mauvais côté.
     */
    const formationsUtilisables = useMemo(() => formationsProposees(donnees), [donnees]);
    const nomsDe = useCallback(
        (ids) => ids.map((id) => (donnees.formations || []).find((f) => f.id === id)?.nom).filter(Boolean),
        [donnees.formations]
    );

    // ── Ouverture / fermeture de l'éditeur ──────────────────────────────────

    const ouvrir = useCallback((cible) => {
        setErreur(null);
        // Reclic sur la même ligne : on referme (S30).
        if (ouvert === cible) { setOuvert(null); setBrouillon(null); return; }

        if (cible === 'new') {
            setOuvert('new');
            setBrouillon({
                // L3a (E171) : les formations cochées (identifiants), le nom. E176 (v90) : plus de semestre.
                // E178 (v91) : les régimes suivis de chaque formation — { id: ['initiale', …] }.
                formations: [], regimes: {}, semestre: '', nom: '',
                date_debut: '', date_fin: '',
                modele_fiche_liaison_id: modeles.fiche_liaison?.[0]?.id || '',
                modele_visite_id:        modeles.visite?.[0]?.id || '',
                modele_eval_id:          modeles.eval?.[0]?.id || '',
                modele_documents_retour_id: modeles.documents_retour?.[0]?.id || '',
                // `null` = « le serveur appliquera ses valeurs par défaut ». On ne
                // recopie pas ces valeurs ici : deux jeux de défauts finiraient par
                // différer, et celui de l'écran n'est jamais celui qui s'écrit.
                etapes_config: null
            });
            return;
        }

        const c = configurations.find((x) => x.id === cible);
        if (!c) return;
        setOuvert(cible);
        setBrouillon({
            formations: (c.formations || []).map((f) => f.sous_niveau_id),
            regimes: Object.fromEntries((c.formations || []).map((f) => [f.sous_niveau_id, f.regimes || ['initiale']])),
            semestre: c.semestre,
            nom: c.nom || '',
            date_debut: pourInputDate(c.date_debut),
            date_fin: pourInputDate(c.date_fin),
            modele_fiche_liaison_id: c.modele_fiche_liaison_id || '',
            modele_visite_id: c.modele_visite_id || '',
            modele_eval_id: c.modele_eval_id || '',
            modele_documents_retour_id: c.modele_documents_retour_id || '',
            etapes_config: c.etapes_config || null,
            // E101 : en lecture seulement — ils verrouillent le choix de la grille.
            // ⚠️ Recopiés ICI, sinon absents : ce brouillon est une liste blanche.
            grille_eval_figee_le: c.grille_eval_figee_le || null,
            grille_eval_figee_nom: c.grille_eval_figee_nom || null,
            // Lot 3.8b (E112) : même liste blanche pour la liste des documents figée.
            documents_retour_figes_le: c.documents_retour_figes_le || null,
            documents_retour_figes_nom: c.documents_retour_figes_nom || null,
            // Lot 3.8c (E112) : et pour la fiche de liaison figée.
            fiche_liaison_figee_le: c.fiche_liaison_figee_le || null,
            fiche_liaison_figee_nom: c.fiche_liaison_figee_nom || null,
            // Lot 3.5c (E162) : et pour la fiche de visite, figée au premier enregistrement d'une fiche.
            visite_figee_le: c.visite_figee_le || null,
            visite_figee_nom: c.visite_figee_nom || null,
            // L2a (E170) : le nom des copies du processus (affiché en vignette à la place du choix du modèle).
            modele_fiche_liaison_nom: c.modele_fiche_liaison_nom || null,
            modele_visite_nom: c.modele_visite_nom || null,
            modele_eval_nom: c.modele_eval_nom || null,
            modele_documents_retour_nom: c.modele_documents_retour_nom || null
        });
    }, [ouvert, configurations, modeles]);

    const fermer = useCallback(() => { setOuvert(null); setBrouillon(null); setErreur(null); }, []);

    // E169 : dans une vignette, l'éditeur de SON processus est toujours ouvert (au montage, après un enregistrement).
    useEffect(() => {
        if (seulement && ouvert === null && configurations.some((c) => c.id === seulement)) ouvrir(seulement);
    }, [seulement, ouvert, configurations, ouvrir]);
    const affichees = seulement ? configurations.filter((c) => c.id === seulement) : configurations;

    // ── Modification du brouillon ───────────────────────────────────────────

    const poser = useCallback((champ, valeur) => {
        setBrouillon((b) => (b ? { ...b, [champ]: valeur } : b));
    }, []);

    /**
     * Modifie une étape du brouillon.
     *
     * ⚠️ `etapes_config` peut valoir `null` (création : le serveur décidera). Dès
     * qu'on touche une étape, il faut un objet — mais on ne le fabrique QUE sur
     * les clés touchées, et le serveur complète le reste. Écrire ici un jeu
     * complet de valeurs par défaut aurait fait de cet écran une seconde source
     * de vérité pour les délais.
     */
    const poserEtape = useCallback((cle, morceau) => {
        setBrouillon((b) => {
            if (!b) return b;
            const ec = b.etapes_config || {};
            return { ...b, etapes_config: { ...ec, [cle]: { ...(ec[cle] || {}), ...morceau } } };
        });
    }, []);

    const poserMode = useCallback((cleMode, morceau) => {
        setBrouillon((b) => {
            if (!b) return b;
            const ec = b.etapes_config || {};
            const e6 = ec.etape6 || {};
            const modes = e6.modes || {};
            return {
                ...b,
                etapes_config: {
                    ...ec,
                    etape6: { ...e6, modes: { ...modes, [cleMode]: { ...(modes[cleMode] || {}), ...morceau } } }
                }
            };
        });
    }, []);

    /** L'état d'une étape dans le brouillon, en retombant sur « actif ». */
    const etapeActive = useCallback((cle, obligatoire) => {
        if (obligatoire) return true;
        return brouillon?.etapes_config?.[cle]?.actif !== false;
    }, [brouillon]);

    const valeurEtape = useCallback((cle, champ, defaut) => {
        const v = brouillon?.etapes_config?.[cle]?.[champ];
        return v === undefined || v === null ? defaut : v;
    }, [brouillon]);

    const valeurMode = useCallback((cleMode, champ, defaut) => {
        const v = brouillon?.etapes_config?.etape6?.modes?.[cleMode]?.[champ];
        return v === undefined || v === null ? defaut : v;
    }, [brouillon]);

    // ── Écritures ───────────────────────────────────────────────────────────

    const enregistrer = useCallback(async () => {
        if (!brouillon) return;
        setEnCours(true);
        setErreur(null);
        try {
            // L3a (E171) : les formations ne partent que si elles ont CHANGÉ — un processus existant dont une formation
            // n'a plus d'étudiant FI doit rester enregistrable (ses dates, ses étapes).
            const avant = ouvert === 'new' ? null : (configurations.find((c) => c.id === ouvert)?.formations || []).map((f) => f.sous_niveau_id);
            // E178 (v91) : un régime coché ou décoché est un changement de formations, lui aussi.
            const regimesAvant = ouvert === 'new' ? {} : Object.fromEntries((configurations.find((c) => c.id === ouvert)?.formations || [])
                .map((f) => [f.sous_niveau_id, [...(f.regimes || [])].sort().join(',')]));
            const regimesDe = (id) => brouillon.regimes?.[id] || ['initiale'];
            const changees = !avant || avant.length !== brouillon.formations.length || avant.some((id) => !brouillon.formations.includes(id))
                || brouillon.formations.some((id) => regimesAvant[id] !== [...regimesDe(id)].sort().join(','));
            const nomSaisi = (brouillon.nom || '').trim();
            // E176 (v90) : plus de semestre — un processus d'avant la v90 garde le sien (il est dans son nom calculé).
            const libelle = ouvert !== 'new' ? brouillon.semestre : null;
            const charge = {
                ...(changees ? { formations: brouillon.formations.map((id) => ({ sous_niveau_id: id, regimes: regimesDe(id) })) } : {}),
                // Le nom proposé n'est pas enregistré : il suit les formations. Vide = le nom calculé.
                nom: nomSaisi && nomSaisi !== nomPropose(nomsDe(brouillon.formations), libelle) ? nomSaisi : null,
                date_debut: brouillon.date_debut || null,
                date_fin: brouillon.date_fin || null,
                modele_fiche_liaison_id: brouillon.modele_fiche_liaison_id || null,
                modele_visite_id: brouillon.modele_visite_id || null,
                modele_eval_id: brouillon.modele_eval_id || null,
                modele_documents_retour_id: brouillon.modele_documents_retour_id || null,
                etapes_config: brouillon.etapes_config
            };

            const r = ouvert === 'new'
                ? await stageAPI.creerConfiguration(departementId, anneeId, charge)
                : await stageAPI.modifierConfiguration(ouvert, departementId, charge);

            onMiseAJour({ configurations: r.data.configurations });
            setOuvert(null);
            setBrouillon(null);
            if (onEcrit) onEcrit();

            // Confirmation APRÈS l'acte : elle dit ce qui s'est passé, y compris
            // ce que l'utilisateur ne voit pas — les dossiers ouverts en silence.
            setBilan({
                titre: ouvert === 'new' ? 'Processus créé' : 'Processus enregistré',
                lignes: [
                    r.data.dossiers_crees > 0
                        ? `${r.data.dossiers_crees} dossier(s) de stage ouvert(s) pour les étudiants en formation initiale de ce processus.`
                        // Une formation seulement retirée : « aucun nouveau dossier » n'apprendrait rien.
                        : r.data.dossiers_retires > 0 ? null : "Aucun nouveau dossier : tous les étudiants concernés en avaient déjà un.",
                    r.data.dates_propagees > 0
                        ? `${r.data.dates_propagees} dossier(s) encore en attente ont reçu les nouvelles dates.`
                        : null,
                    r.data.dossiers_retires > 0
                        ? `${r.data.dossiers_retires} dossier(s) encore en attente retiré(s) avec la ou les formations décochées.`
                        : null
                ].filter(Boolean)
            });
        } catch (e) {
            setErreur(e.response?.data?.error || e.message || "L'enregistrement a échoué");
        } finally {
            setEnCours(false);
        }
    }, [brouillon, ouvert, departementId, anneeId, onMiseAJour, onEcrit, configurations, donnees, nomsDe]);

    const dupliquer = useCallback(async (id) => {
        setEnCours(true);
        setErreur(null);
        try {
            // Une duplication ouvre l'éditeur pré-rempli (dates, modèles, étapes) SANS formation : les cocher est un
            // choix (E176 : une même formation peut être suivie dans plusieurs processus — ses étudiants y auraient
            // alors un second dossier).
            const c = configurations.find((x) => x.id === id);
            if (!c) return;
            setOuvert('new');
            setBrouillon({
                formations: [], regimes: {}, semestre: '', nom: '',
                date_debut: pourInputDate(c.date_debut),
                date_fin: pourInputDate(c.date_fin),
                modele_fiche_liaison_id: c.modele_fiche_liaison_id || '',
                modele_visite_id: c.modele_visite_id || '',
                modele_eval_id: c.modele_eval_id || '',
                modele_documents_retour_id: c.modele_documents_retour_id || '',
                etapes_config: c.etapes_config || null
            });
        } finally {
            setEnCours(false);
        }
    }, [configurations]);

    const supprimer = useCallback(async () => {
        if (!aSupprimer) return;
        setEnCours(true);
        try {
            const r = await stageAPI.supprimerConfiguration(aSupprimer.id, departementId);
            onMiseAJour({ configurations: r.data.configurations });
            setASupprimer(null);
            if (ouvert === aSupprimer.id) { setOuvert(null); setBrouillon(null); }
            setBilan({
                titre: 'Processus supprimé',
                lignes: [`${r.data.dossiers_supprimes} dossier(s) en attente ont été supprimés avec elle.`],
                // E169 : la vignette du processus supprimé n'a plus d'objet — retour à l'accueil à la fermeture.
                apres: aSupprimer.id === seulement ? onSupprime : null
            });
        } catch (e) {
            setASupprimer(null);
            setErreur(e.response?.data?.detail || e.response?.data?.error || 'La suppression a échoué');
        } finally {
            setEnCours(false);
        }
    }, [aSupprimer, departementId, ouvert, onMiseAJour, seulement, onSupprime]);

    const recupererN1 = useCallback(async () => {
        setEnCours(true);
        setErreur(null);
        try {
            const r = await stageAPI.recupererN1(departementId, anneeId);
            onMiseAJour({ configurations: r.data.configurations });
            setConfirmeN1(false);
            setBilan({
                titre: `Processus repris de ${r.data.annee_source.libelle}`,
                lignes: [
                    `${r.data.copiees.length} processus copié(s) — sans leurs dates, qu'il reste à saisir.`,
                    ...r.data.copiees.map((c) => `· ${c.formation}${c.semestre ? ` ${c.semestre}` : ''} — ${c.dossiers_crees} dossier(s) ouvert(s)`),
                    ...(r.data.ignorees.length
                        ? [`${r.data.ignorees.length} ignorée(s) :`,
                           ...r.data.ignorees.map((c) => `· ${c.formation}${c.semestre ? ` ${c.semestre}` : ''} — ${c.motifs.join(' ; ')}`)]
                        : [])
                ]
            });
        } catch (e) {
            setConfirmeN1(false);
            setErreur(e.response?.data?.detail || e.response?.data?.error || 'La reprise a échoué');
        } finally {
            setEnCours(false);
        }
    }, [departementId, anneeId, onMiseAJour]);

    // ── L'ÉDITEUR ───────────────────────────────────────────────────────────

    /**
     * « Reste à faire » (Bertrand, 2026-10-21) — un champ SIGNALÉ manquant (`manquants`, après un enregistrement ou un
     * clic sur la ligne du haut) s'encadre de rouge ; il redevient normal dès qu'on le remplit, sans attendre d'enregistrer.
     */
    const rempli = {
        dates: Boolean(brouillon?.date_debut && brouillon?.date_fin),
        modele_fiche_liaison_id: Boolean(brouillon?.modele_fiche_liaison_id),
        modele_documents_retour_id: Boolean(brouillon?.modele_documents_retour_id),
        modele_visite_id: Boolean(brouillon?.modele_visite_id),
        modele_eval_id: Boolean(brouillon?.modele_eval_id)
    };
    const rouge = (cle) => manquants.includes(cle) && !rempli[cle];
    const classeReste = (cle, base) => base + (rouge(cle) ? ' reste-manquant' : '');

    // L3a (E171) — ce que montre le bloc « Formations suivies ».
    const enEdition = ouvert && ouvert !== 'new' ? configurations.find((c) => c.id === ouvert) : null;
    const lignesFormations = brouillon ? [...new Set([...(brouillon.formations || []), ...(enEdition?.formations || []).map((x) => x.sous_niveau_id)])]
        .map((id) => {
            const f = (donnees.formations || []).find((x) => x.id === id);
            const avant = (enEdition?.formations || []).find((x) => x.sous_niveau_id === id);
            return { id, nom: f?.nom || avant?.nom || `#${id}`, stagiaires: f?.nb_etudiants_fi || 0, formation: f || null,
                     dossiers: avant ? Number(avant.nb_dossiers) : null, engages: avant ? Number(avant.nb_engages) : 0,
                     retiree: !(brouillon.formations || []).includes(id), nouvelle: Boolean(enEdition) && !avant,
                     // E178 (v91) : les régimes cochés (brouillon) et ceux enregistrés (pour dire ce qu'un décochage retire)
                     regimes: brouillon.regimes?.[id] || ['initiale'], regimesAvant: avant?.regimes || null };
        }) : [];
    const basculerFormation = (id) => setBrouillon((b) => (b ? {
        ...b, formations: dansLOrdre(donnees, b.formations.includes(id) ? b.formations.filter((x) => x !== id) : [...b.formations, id]),
        // E178 (Q1) : une formation nouvellement cochée suit la formation initiale seule.
        regimes: b.regimes?.[id] ? b.regimes : { ...(b.regimes || {}), [id]: ['initiale'] }
    } : b));
    // E178 (v91) : cocher / décocher un régime d'une formation (« Qui est suivi ici »).
    const basculerRegime = (id, regime) => setBrouillon((b) => {
        if (!b) return b;
        const r = b.regimes?.[id] || ['initiale'];
        return { ...b, regimes: { ...(b.regimes || {}), [id]: r.includes(regime) ? r.filter((x) => x !== regime) : [...r, regime] } };
    });

    const editeur = (
        <tr>
            <td colSpan={4} className="cfg-editor-cell">
                <div className="cfg-editor">

                    <div className="cfg-editor-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <i className="fa-solid fa-pen" style={{ color: '#16a34a', fontSize: 12 }} />
                            <span style={{ fontSize: 13, fontWeight: 700 }}>
                                {ouvert === 'new' ? 'Nouveau processus' : 'Processus'}
                            </span>
                            {ouvert !== 'new' && (
                                <span style={{ fontSize: 12, color: '#64748b' }}>
                                    {configurations.find((c) => c.id === ouvert)?.titre}
                                </span>
                            )}
                        </div>
                        {/* E169 : dans une vignette, l'éditeur ne se ferme pas (« Annuler » revient au dernier enregistrement). */}
                        {(!seulement || ouvert !== seulement) && (
                        <button type="button" className="action-btn" onClick={fermer} title="Fermer l'éditeur">
                            <i className="fa-solid fa-xmark" />
                        </button>
                        )}
                    </div>

                    {erreur && (
                        <div className="stg-encart stg-encart-erreur">
                            <i className="fa-solid fa-circle-exclamation" />
                            <span>{erreur}</span>
                        </div>
                    )}

                    {/* L3a (E171) — LES FORMATIONS SUIVIES (maquette rév. 3, `rendreFormations`) : plusieurs. Une formation
                        décochée emporte ses dossiers encore en attente. E176 (v90, maquette rév. 13) : plus de semestre ;
                        une formation suivie AUSSI dans un autre processus le dit (Q1 — ses étudiants y ont un autre dossier). */}
                    <div className="pf-bloc">
                        <div className="pf-bloc-h">
                            <b><i className="fa-solid fa-users" /> Formations suivies <span className="form-required">*</span></b>
                            <MenuFormations donnees={donnees} coches={brouillon?.formations || []} disabled={!modifiable}
                                            exclureId={ouvert === 'new' ? null : ouvert} onBasculer={basculerFormation} />
                            {ouvert !== 'new' && brouillon?.semestre && <span className="pf-chip">Stage {brouillon.semestre}</span>}
                            <span>plusieurs possibles · plusieurs processus par formation possibles</span>
                        </div>
                        {lignesFormations.length
                            ? <table>
                                <thead><tr><th>Formation</th><th>Qui est suivi ici</th><th>Ce que ça donne</th></tr></thead>
                                <tbody>
                                    {lignesFormations.map((l) => (
                                        <tr key={l.id} className={l.retiree ? 'retiree' : ''}>
                                            <td><b>{l.nom}</b>
                                                {l.dossiers !== null && <div className="pf-dossiers">{l.dossiers} dossier{l.dossiers > 1 ? 's' : ''}</div>}
                                                {(() => {
                                                    const ailleurs = autresProcessus(configurations, l.id, ouvert === 'new' ? null : ouvert);
                                                    return !l.retiree && ailleurs.length > 0 && (
                                                        <div className="pf-ailleurs-ligne"><i className="fa-solid fa-circle-info" /> aussi suivie dans « {ailleurs.join(' », « ')} » : ses étudiants y ont aussi un dossier</div>
                                                    );
                                                })()}
                                            </td>
                                            {/* E178 (v91, maquette rév. 15) — QUI EST SUIVI ICI : une case par régime présent (ou déjà coché). */}
                                            <td className="reg-cases">
                                                {!l.retiree && REGIMES.filter((r) => r.nb(l.formation) > 0 || l.regimes.includes(r.cle)).map((r) => (
                                                    <label key={r.cle} className={'reg-case' + (r.cle !== 'initiale' ? ' alt' : '')}>
                                                        <input type="checkbox" checked={l.regimes.includes(r.cle)} disabled={!modifiable}
                                                               onChange={() => basculerRegime(l.id, r.cle)} /> {r.libelle} <span>({r.nb(l.formation)})</span>
                                                    </label>
                                                ))}
                                            </td>
                                            <td className="reg-effet">
                                                {!l.retiree
                                                    ? <>{(() => {
                                                        const alt = REGIMES.filter((r) => r.cle !== 'initiale' && r.nb(l.formation) > 0);
                                                        const ici = alt.filter((r) => l.regimes.includes(r.cle)), cfa = alt.filter((r) => !l.regimes.includes(r.cle));
                                                        const noms = (rs) => rs.map((r) => r.libelle.toLowerCase()).join(' et ').replace(/^./, (c) => c.toUpperCase());
                                                        const decoches = (l.regimesAvant || []).filter((r) => !l.regimes.includes(r));
                                                        return <>
                                                            {l.regimes.includes('initiale') && <div>Stagiaires : les étapes ① → ⑪ ci-dessous</div>}
                                                            {ici.length > 0 && <div>{noms(ici)} : suivis <b>ici</b>, mêmes étapes <b>sans convention ni documents de retour</b> (⑦ ⑧), tuteur du registre</div>}
                                                            {cfa.length > 0 && <div className="reg-cfa">{noms(cfa)} : suivis par le <b>CFA</b></div>}
                                                            {!l.regimes.length && <div className="reg-alerte"><i className="fa-solid fa-triangle-exclamation" /> Aucun régime coché : personne n'est suivi dans cette formation</div>}
                                                            {(l.formation?.nb_sans_regime || 0) > 0 && (
                                                                <div className="reg-sans"><i className="fa-solid fa-circle-exclamation" /> <b>{l.formation.nb_sans_regime} étudiant(s) sans régime</b> : aucun dossier — à compléter dans le module Étudiants</div>)}
                                                            {decoches.length > 0 && (
                                                                <div className="reg-alerte"><i className="fa-solid fa-circle-info" /> Décoché : les dossiers en attente {decoches.map((r) => ({ initiale: 'de la formation initiale', apprentis: 'des apprentis', fc: 'de la formation continue' })[r]).join(' et ')} seront retirés à l'enregistrement</div>)}
                                                            {(ouvert === 'new' || l.nouvelle) && <div className="reg-note">Ses dossiers sont ouverts à l'enregistrement.</div>}
                                                        </>;
                                                    })()}</>
                                                    : l.engages > 0
                                                        ? <><i className="fa-solid fa-triangle-exclamation" /> {l.engages} dossier(s) ont avancé : elle ne peut pas être retirée</>
                                                        : <><i className="fa-solid fa-circle-info" /> Décochée : {l.dossiers} dossier(s) en attente retiré(s) à l'enregistrement</>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            : <div style={{ color: '#c2410c', fontSize: 12 }}>Aucune formation cochée.</div>}
                    </div>

                    <div className="cfg-new-fields">
                        <div className="pf-nom">
                            <div>
                                <label htmlFor="cfg-nom">Nom de la vignette</label>
                                <input id="cfg-nom" className="input-sm" style={{ width: '100%' }} maxLength={200} disabled={!modifiable}
                                       value={brouillon?.nom || ''} placeholder={nomPropose(nomsDe(brouillon?.formations || []), ouvert === 'new' ? null : brouillon?.semestre)}
                                       title="Vide : le nom est calculé à partir des formations"
                                       onChange={(e) => poser('nom', e.target.value)} />
                            </div>
                            <div data-reste="dates" className={classeReste('dates', '')}>
                                <label htmlFor="cfg-debut">Date début</label>
                                <ChampDate
                                    id="cfg-debut" className="input-sm" disabled={!modifiable}
                                    value={brouillon?.date_debut || ''}
                                    onChange={(e) => poser('date_debut', e.target.value)}
                                />
                            </div>
                            <div className={classeReste('dates', '')}>
                                <label htmlFor="cfg-fin">Date fin{rouge('dates') && <span className="reste-note"> — à remplir pour terminer le paramétrage</span>}</label>
                                <ChampDate
                                    id="cfg-fin" className="input-sm" disabled={!modifiable}
                                    value={brouillon?.date_fin || ''}
                                    onChange={(e) => poser('date_fin', e.target.value)}
                                />
                            </div>
                        </div>
                        {ouvert === 'new' && (
                            <div className="stg-encart stg-encart-alerte" style={{ marginBottom: 0, marginTop: 10 }}>
                                <i className="fa-solid fa-triangle-exclamation" />
                                <span>
                                    Seules les formations ayant des étudiants en formation initiale sont
                                    proposées. Enregistrer ouvrira un dossier de stage pour chacun d'eux.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* ── LES DIX ÉTAPES ─────────────────────────────────── */}
                    <div className="cfg-steps">
                        {etapes.map((e) => {
                            const active = etapeActive(e.cle, e.obligatoire);
                            return (
                                <div key={e.cle} className={'cfg-step' + (active ? '' : ' inactive')}>
                                    <div className="cfg-step-header">
                                        <span className="cfg-step-num" aria-hidden="true">{NUMEROS[e.numero - 1]}</span>
                                        <div style={{ flex: 1 }}>
                                            <div className="cfg-step-title">
                                                <i
                                                    className={'fa-solid ' + (ICONES_ETAPE[e.cle] || 'fa-circle')}
                                                    style={{ color: e.obligatoire ? '#16a34a' : '#2563eb', fontSize: 10, marginRight: 4 }}
                                                />
                                                {e.libelle}
                                            </div>
                                            {SOUS_TITRES[e.cle] && <div className="cfg-step-sous">{SOUS_TITRES[e.cle]}</div>}
                                        </div>

                                        {e.obligatoire && (
                                            <span className="cfg-badge-oblig">
                                                <i className="fa-solid fa-lock" style={{ fontSize: 9 }} /> Obligatoire
                                            </span>
                                        )}

                                        <button
                                            type="button"
                                            className={'toggle' + (active ? ' on' : '') + (e.obligatoire ? ' cfg-locked' : '')}
                                            aria-pressed={active}
                                            aria-label={`Étape ${e.numero} — ${e.libelle}`}
                                            disabled={e.obligatoire || !modifiable}
                                            title={e.obligatoire
                                                ? 'Étape obligatoire : elle ne peut pas être désactivée'
                                                : (active ? 'Désactiver cette étape' : 'Activer cette étape')}
                                            onClick={() => poserEtape(e.cle, { actif: !active })}
                                        />
                                    </div>

                                    {/* Le corps n'existe que pour les étapes qui ont des
                                        paramètres. Les étapes automatiques (②⑤⑪) n'en ont
                                        aucun — la maquette ne leur dessine pas de corps. */}
                                    {!e.automatique && (
                                        <div className="cfg-step-body">

                                            {e.cle === 'etape1' && (
                                                <>
                                                    <div data-reste="modele_fiche_liaison_id" className={classeReste('modele_fiche_liaison_id', 'cfg-param-row')}>
                                                        <label htmlFor="cfg-mod-fiche">Modèle fiche de liaison{rouge('modele_fiche_liaison_id') && <span className="reste-note"> — à remplir pour terminer le paramétrage</span>}</label>
                                                        {/* ⚠️ E112 : une campagne LANCÉE garde sa fiche de liaison. */}
                                                        {seulement ? (
                                                            <ModeleDuProcessus nom={brouillon?.modele_fiche_liaison_nom} />
                                                        ) : (
                                                        <select
                                                            id="cfg-mod-fiche" className="input-sm" style={{ flex: 1 }}
                                                            disabled={!modifiable || !!brouillon?.fiche_liaison_figee_le}
                                                            value={brouillon?.modele_fiche_liaison_id || ''}
                                                            onChange={(ev) => poser('modele_fiche_liaison_id', ev.target.value)}
                                                        >
                                                            <option value="">— Aucun —</option>
                                                            {(modeles.fiche_liaison || []).map((m) => (
                                                                <option key={m.id} value={m.id}>{m.nom}</option>
                                                            ))}
                                                        </select>
                                                        )}
                                                    </div>
                                                    {brouillon?.fiche_liaison_figee_le && (
                                                        <div className="cfg-param-row" style={{ fontSize: 11, color: '#64748b' }}>
                                                            <span>
                                                                <i className="fa-solid fa-lock" /> Fiche figée le{' '}
                                                                {new Date(brouillon.fiche_liaison_figee_le).toLocaleDateString('fr-FR')} (premier envoi du lien) :
                                                                {' '}étudiants et maîtres de stage remplissent « {brouillon.fiche_liaison_figee_nom} ». Un autre
                                                                modèle, ou une modification du modèle, vaudra pour les copies de ce processus (Dupliquer, Récupérer N-1).
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="cfg-param-row">
                                                        <label htmlFor="cfg-d1">Délai relance si non remplie</label>
                                                        <div className="cfg-delai">
                                                            <input
                                                                id="cfg-d1" type="number" min="1" max="52" className="input-sm"
                                                                disabled={!modifiable}
                                                                value={valeurEtape('etape1', 'delai_relance_sem', 2)}
                                                                onChange={(ev) => poserEtape('etape1', { delai_relance_sem: ev.target.value })}
                                                            />
                                                            <span>sem.</span>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {e.cle === 'etape3' && (
                                                <div className="cfg-param-row">
                                                    <label htmlFor="cfg-d3">Délai relance si fiche non envoyée</label>
                                                    <div className="cfg-delai">
                                                        <input
                                                            id="cfg-d3" type="number" min="1" max="52" className="input-sm"
                                                            disabled={!modifiable}
                                                            value={valeurEtape('etape3', 'delai_relance_sem', 1)}
                                                            onChange={(ev) => poserEtape('etape3', { delai_relance_sem: ev.target.value })}
                                                        />
                                                        <span>sem.</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* E173 : ③ — le délai, et QUI décide (le serveur le vérifie). */}
                                            {e.cle === 'etape_resp' && (
                                                <>
                                                    <div className="cfg-param-row">
                                                        <label htmlFor="cfg-dresp">Délai max. pour valider la fiche</label>
                                                        <div className="cfg-delai">
                                                            <input
                                                                id="cfg-dresp" type="number" min="1" max="52" className="input-sm"
                                                                disabled={!modifiable || !active}
                                                                value={valeurEtape('etape_resp', 'delai_validation_sem', 1)}
                                                                onChange={(ev) => poserEtape('etape_resp', { delai_validation_sem: ev.target.value })}
                                                            />
                                                            <span>sem.</span>
                                                        </div>
                                                    </div>
                                                    <div className="cfg-note">
                                                        <i className="fa-solid fa-user-shield" /> Réservée aux <b>responsables des stages</b> (Paramètres
                                                        généraux › Équipe) et aux administrateurs · trois gestes : <b>Valider</b> (l'envoi au maître
                                                        devient possible) · <b>Renvoyer à l'étudiant</b> (commentaire, nouveau lien) · <b>Refuser le stage</b>.
                                                    </div>
                                                </>
                                            )}

                                            {e.cle === 'etape5' && (
                                                <div className="cfg-param-row">
                                                    <label htmlFor="cfg-d5">Délai max. pour valider la fiche</label>
                                                    <div className="cfg-delai">
                                                        <input
                                                            id="cfg-d5" type="number" min="1" max="52" className="input-sm"
                                                            disabled={!modifiable}
                                                            value={valeurEtape('etape5', 'delai_validation_sem', 1)}
                                                            onChange={(ev) => poserEtape('etape5', { delai_validation_sem: ev.target.value })}
                                                        />
                                                        <span>sem.</span>
                                                    </div>
                                                </div>
                                            )}

                                            {e.cle === 'etape6' && (
                                                <>
                                                    <div className="cfg-param-row">
                                                        <label htmlFor="cfg-d6">Délai relance si non retournée</label>
                                                        <div className="cfg-delai">
                                                            <input
                                                                id="cfg-d6" type="number" min="1" max="52" className="input-sm"
                                                                disabled={!modifiable}
                                                                value={valeurEtape('etape6', 'delai_relance_sem', 2)}
                                                                onChange={(ev) => poserEtape('etape6', { delai_relance_sem: ev.target.value })}
                                                            />
                                                            <span>sem.</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>
                                                        Modes de convention disponibles :
                                                    </div>
                                                    <div className="cfg-modes">
                                                        {modesRef.map((m) => {
                                                            const actifMode = valeurMode(m.cle, 'actif', true);
                                                            return (
                                                                <div className="cfg-mode" key={m.cle}>
                                                                    <label className="cfg-mode-header">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={actifMode}
                                                                            disabled={!modifiable}
                                                                            onChange={() => poserMode(m.cle, { actif: !actifMode })}
                                                                        />
                                                                        <i className={'fa-solid ' + (ICONES_MODE[m.cle] || 'fa-file')} />
                                                                        <span>{m.libelle}</span>
                                                                    </label>
                                                                    <div className="cfg-mode-body">
                                                                        <div className="lbl">Texte email</div>
                                                                        <textarea
                                                                            className="input-sm"
                                                                            style={{ marginTop: 0 }}
                                                                            disabled={!modifiable}
                                                                            aria-label={`Texte du mode ${m.libelle}`}
                                                                            value={valeurMode(m.cle, 'texte', m.texte)}
                                                                            onChange={(ev) => poserMode(m.cle, { texte: ev.target.value })}
                                                                        />
                                                                        <div className="cfg-mode-dest">
                                                                            {m.destinataires === 'maitre_to_etudiant_cc'
                                                                                ? 'À : Maître de stage · Cc : Étudiant'
                                                                                : 'À : Étudiant · Cc : Maître de stage'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}

                                            {e.cle === 'etape7' && (
                                                <>
                                                    <div data-reste="modele_documents_retour_id" className={classeReste('modele_documents_retour_id', 'cfg-param-row')}>
                                                        <label htmlFor="cfg-mod-docs">Modèle de documents{rouge('modele_documents_retour_id') && <span className="reste-note"> — à remplir pour terminer le paramétrage</span>}</label>
                                                        {/* ⚠️ E112 : une campagne LANCÉE garde sa liste de documents. */}
                                                        {seulement ? (
                                                            <ModeleDuProcessus nom={brouillon?.modele_documents_retour_nom} />
                                                        ) : (
                                                        <select
                                                            id="cfg-mod-docs" className="input-sm" style={{ flex: 1 }}
                                                            disabled={!modifiable || !!brouillon?.documents_retour_figes_le}
                                                            value={brouillon?.modele_documents_retour_id || ''}
                                                            onChange={(ev) => poser('modele_documents_retour_id', ev.target.value)}
                                                        >
                                                            <option value="">— Aucun —</option>
                                                            {(modeles.documents_retour || []).map((m) => (
                                                                <option key={m.id} value={m.id}>{m.nom}</option>
                                                            ))}
                                                        </select>
                                                        )}
                                                    </div>
                                                    {brouillon?.documents_retour_figes_le && (
                                                        <div className="cfg-param-row" style={{ fontSize: 11, color: '#64748b' }}>
                                                            <span>
                                                                <i className="fa-solid fa-lock" /> Liste figée le{' '}
                                                                {new Date(brouillon.documents_retour_figes_le).toLocaleDateString('fr-FR')} (premier envoi de la convention) :
                                                                {' '}toute la campagne dépose selon « {brouillon.documents_retour_figes_nom} ». Un autre
                                                                modèle, ou une modification du modèle, vaudra pour les copies de ce processus (Dupliquer, Récupérer N-1).
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="cfg-param-row">
                                                        <label htmlFor="cfg-d7">Délai relance si non déposés</label>
                                                        <div className="cfg-delai">
                                                            <input
                                                                id="cfg-d7" type="number" min="1" max="52" className="input-sm"
                                                                disabled={!modifiable}
                                                                value={valeurEtape('etape7', 'delai_relance_sem', 2)}
                                                                onChange={(ev) => poserEtape('etape7', { delai_relance_sem: ev.target.value })}
                                                            />
                                                            <span>sem.</span>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {e.cle === 'etape8' && (
                                                <>
                                                    <div data-reste="modele_visite_id" className={classeReste('modele_visite_id', 'cfg-param-row')}>
                                                        <label htmlFor="cfg-mod-visite">Modèle fiche de visite{rouge('modele_visite_id') && <span className="reste-note"> — à remplir pour terminer le paramétrage</span>}</label>
                                                        {/* ⚠️ Lot 3.5c (E101, E162) : une campagne dont une fiche est enregistrée garde SA fiche. */}
                                                        {seulement ? (
                                                            <ModeleDuProcessus nom={brouillon?.modele_visite_nom} />
                                                        ) : (
                                                        <select
                                                            id="cfg-mod-visite" className="input-sm" style={{ flex: 1 }}
                                                            disabled={!modifiable || !!brouillon?.visite_figee_le}
                                                            value={brouillon?.modele_visite_id || ''}
                                                            onChange={(ev) => poser('modele_visite_id', ev.target.value)}
                                                        >
                                                            <option value="">— Aucun —</option>
                                                            {(modeles.visite || []).map((m) => (
                                                                <option key={m.id} value={m.id}>{m.nom}</option>
                                                            ))}
                                                        </select>
                                                        )}
                                                    </div>
                                                    {brouillon?.visite_figee_le && (
                                                        <div className="cfg-param-row" style={{ fontSize: 11, color: '#64748b' }}>
                                                            <span>
                                                                <i className="fa-solid fa-lock" /> Fiche figée le{' '}
                                                                {new Date(brouillon.visite_figee_le).toLocaleDateString('fr-FR')} (première fiche de visite enregistrée) :
                                                                {' '}toute la campagne remplit « {brouillon.visite_figee_nom} ». Un autre
                                                                modèle, ou une modification du modèle, vaudra pour les copies de ce processus (Dupliquer, Récupérer N-1).
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {e.cle === 'etape9' && (
                                                <>
                                                    <div data-reste="modele_eval_id" className={classeReste('modele_eval_id', 'cfg-param-row')}>
                                                        <label htmlFor="cfg-mod-eval">Modèle grille{rouge('modele_eval_id') && <span className="reste-note"> — à remplir pour terminer le paramétrage</span>}</label>
                                                        {/* ⚠️ E101 : une campagne LANCÉE garde sa grille — le
                                                            serveur refuse de la changer, l'écran le dit avant. */}
                                                        {seulement ? (
                                                            <ModeleDuProcessus nom={brouillon?.modele_eval_nom} />
                                                        ) : (
                                                        <select
                                                            id="cfg-mod-eval" className="input-sm" style={{ flex: 1 }}
                                                            disabled={!modifiable || !!brouillon?.grille_eval_figee_le}
                                                            value={brouillon?.modele_eval_id || ''}
                                                            onChange={(ev) => poser('modele_eval_id', ev.target.value)}
                                                        >
                                                            <option value="">— Aucun —</option>
                                                            {(modeles.eval || []).map((m) => (
                                                                <option key={m.id} value={m.id}>{m.nom}</option>
                                                            ))}
                                                        </select>
                                                        )}
                                                    </div>
                                                    {brouillon?.grille_eval_figee_le && (
                                                        <div className="cfg-param-row" style={{ fontSize: 11, color: '#64748b' }}>
                                                            <span>
                                                                <i className="fa-solid fa-lock" /> Grille figée le{' '}
                                                                {new Date(brouillon.grille_eval_figee_le).toLocaleDateString('fr-FR')} (premier envoi) :
                                                                {' '}toute la campagne note sur « {brouillon.grille_eval_figee_nom} ». Un autre
                                                                modèle, ou une modification du modèle, vaudra pour les copies de ce processus (Dupliquer, Récupérer N-1).
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="cfg-param-row">
                                                        <label htmlFor="cfg-d9e">Délai envoi avant fin</label>
                                                        <div className="cfg-delai">
                                                            <input
                                                                id="cfg-d9e" type="number" min="1" max="52" className="input-sm"
                                                                disabled={!modifiable}
                                                                value={valeurEtape('etape9', 'delai_envoi_sem', 3)}
                                                                onChange={(ev) => poserEtape('etape9', { delai_envoi_sem: ev.target.value })}
                                                            />
                                                            <span>sem.</span>
                                                        </div>
                                                    </div>
                                                    <div className="cfg-param-row">
                                                        <label htmlFor="cfg-d9r">Délai relance si non reçue</label>
                                                        <div className="cfg-delai">
                                                            <input
                                                                id="cfg-d9r" type="number" min="1" max="52" className="input-sm"
                                                                disabled={!modifiable}
                                                                value={valeurEtape('etape9', 'delai_relance_sem', 1)}
                                                                onChange={(ev) => poserEtape('etape9', { delai_relance_sem: ev.target.value })}
                                                            />
                                                            <span>sem.</span>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                        <button
                            type="button" className="btn btn-green btn-sm"
                            disabled={enCours || !modifiable}
                            onClick={enregistrer}
                        >
                            {enCours
                                ? <><i className="fa-solid fa-circle-notch stg-tourne" /> Enregistrement…</>
                                : <><i className="fa-solid fa-check" /> Enregistrer</>}
                        </button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={fermer} disabled={enCours}
                                title={seulement && ouvert === seulement ? 'Revenir au dernier enregistrement' : undefined}>
                            Annuler
                        </button>
                        {/* E177 (maquette rév. 14) : campagne PAS lancée → supprimer ici aussi ; lancée → on le dit (le serveur refuserait). */}
                        {enEdition && (enEdition.nb_dossiers_engages > 0
                            ? <span className="cfg-non-supprimable"><i className="fa-solid fa-lock" /> Campagne lancée{enEdition.fiche_liaison_figee_le
                                ? ` le ${new Date(enEdition.fiche_liaison_figee_le).toLocaleDateString('fr-FR')}` : ''} : ce processus ne se supprime plus</span>
                            : <button type="button" className="btn btn-outline btn-sm cfg-supprimer" disabled={enCours || !modifiable}
                                      onClick={() => setASupprimer(enEdition)}>
                                  <i className="fa-solid fa-trash" /> Supprimer ce processus
                              </button>)}
                    </div>
                </div>
            </td>
        </tr>
    );

    // ── LE TABLEAU ──────────────────────────────────────────────────────────

    return (
        <>
            {/* E169 : dans une vignette, « Récupérer N-1 » et « Nouveau processus » sont sur l'accueil. */}
            {!seulement && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                    <i className="fa-solid fa-circle-info" /> Chaque processus associe une formation ×
                    semestre à un déroulé de stage paramétré par étapes.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        type="button" className="btn btn-outline btn-sm"
                        disabled={!modifiable || enCours}
                        onClick={() => setConfirmeN1(true)}
                        title="Copier les processus de l'année précédente, sans leurs dates"
                    >
                        <i className="fa-solid fa-rotate-left" /> Récupérer N-1
                    </button>
                    <button
                        type="button" className="btn btn-green btn-sm"
                        disabled={!modifiable || enCours || !formationsUtilisables.length}
                        onClick={() => ouvrir('new')}
                        title={formationsUtilisables.length
                            ? 'Créer un processus de stage'
                            : "Aucune formation n'a d'étudiant en formation initiale : vérifiez le module Étudiants"}
                    >
                        <i className="fa-solid fa-plus" /> Nouveau processus
                    </button>
                </div>
            </div>
            )}

            {erreur && !ouvert && (
                <div className="stg-encart stg-encart-erreur">
                    <i className="fa-solid fa-circle-exclamation" />
                    <span>{erreur}</span>
                </div>
            )}

            <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header">
                    <span>
                        <i className="fa-solid fa-diagram-project" /> Processus {donnees.annee?.libelle || ''}
                    </span>
                    {/* La légende vit dans l'en-tête de carte, comme la maquette. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontSize: 10, color: '#64748b', marginRight: 4 }}>Légende :</span>
                        <span className="cfg-chip on locked"><i className="fa-solid fa-lock" /> Obligatoire</span>
                        <span className="cfg-chip on">Actif</span>
                        <span className="cfg-chip off">Inactif</span>
                    </div>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>Formation</th><th>Période</th>
                            <th>Étapes configurées</th><th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {configurations.length === 0 && ouvert !== 'new' && (
                            <tr>
                                <td colSpan={4} style={{ textAlign: 'center', color: '#64748b', padding: '24px 14px' }}>
                                    Aucun processus de stage pour cette année.
                                    {formationsUtilisables.length
                                        ? " Créez-en une, ou reprenez celles de l'année précédente."
                                        : " ⚠ Aucune formation n'a d'étudiant en formation initiale : commencez par le module Étudiants."}
                                </td>
                            </tr>
                        )}

                        {affichees.map((c) => (
                            <React.Fragment key={c.id}>
                                <tr>
                                    {/* L3a (E171) : le nom du processus, et ses formations quand il ne les dit pas déjà. */}
                                    <td>
                                        <strong>{c.titre || c.formation_nom}</strong>
                                        {(c.formations || []).length > 1 && (
                                            <div style={{ fontSize: 11, color: '#64748b' }}>{c.formation_nom}</div>
                                        )}
                                    </td>
                                    <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                                        {c.date_debut || c.date_fin
                                            ? `${jjmm(c.date_debut)} → ${jjmm(c.date_fin)}`
                                            : <span style={{ color: '#b45309' }}>
                                                  <i className="fa-solid fa-triangle-exclamation" /> dates à saisir
                                              </span>}
                                    </td>
                                    {/* ⚠️ La cellule est BORNÉE : sans largeur maximale, une
                                        cellule de tableau prend sa largeur naturelle et le
                                        repli des pastilles n'a jamais lieu (lot 1.4). */}
                                    <td className="cfg-chips-cell">
                                        <div className="cfg-chips">
                                            {(c.chips || []).map((chip) => (
                                                <span
                                                    key={chip.numero}
                                                    className={'cfg-chip ' + (chip.active ? 'on' : 'off')
                                                               + (chip.verrouillee ? ' locked' : '')}
                                                    title={chip.titre}
                                                >
                                                    {chip.verrouillee && <i className="fa-solid fa-lock" />}
                                                    {chip.libelle}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="actions">
                                        {!seulement && (
                                        <button
                                            type="button" className="action-btn" title="Modifier"
                                            onClick={() => ouvrir(c.id)}
                                        >
                                            <i className="fa-solid fa-pen" />
                                        </button>
                                        )}
                                        <button
                                            type="button" className="action-btn" title="Dupliquer"
                                            disabled={!modifiable}
                                            onClick={() => dupliquer(c.id)}
                                        >
                                            <i className="fa-solid fa-copy" />
                                        </button>
                                        <button
                                            type="button" className="action-btn"
                                            title={c.nb_dossiers_engages > 0 ? 'Campagne lancée : ce processus ne se supprime plus' : 'Supprimer ce processus (campagne pas lancée)'}
                                            disabled={!modifiable || c.nb_dossiers_engages > 0}
                                            onClick={() => setASupprimer(c)}
                                        >
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    </td>
                                </tr>
                                {/* S29 : l'éditeur s'insère SOUS la ligne cliquée. */}
                                {ouvert === c.id && editeur}
                            </React.Fragment>
                        ))}

                        {/* S36 : une nouvelle configuration s'ajoute EN BAS du tableau. */}
                        {ouvert === 'new' && editeur}
                    </tbody>
                </table>
            </div>

            {/* ── Récapitulatif AVANT la suppression (règle 6) ───────────── */}
            {aSupprimer && (
                <StageModale
                    titre="Supprimer ce processus ?"
                    icone="fa-trash"
                    ton="danger"
                    enCours={enCours}
                    libelleValider="Supprimer"
                    onValider={supprimer}
                    onFermer={() => setASupprimer(null)}
                >
                    <div>
                        <b>{aSupprimer.titre || aSupprimer.formation_nom}</b>
                    </div>
                    <ul>
                        <li>{aSupprimer.nb_dossiers} dossier(s) de stage y sont rattachés.</li>
                        <li>
                            {aSupprimer.nb_dossiers_engages > 0
                                ? <><b>{aSupprimer.nb_dossiers_engages} ont déjà avancé</b> dans le processus :
                                    la suppression sera refusée pour ne pas les détacher en silence.</>
                                : 'Aucun n\'a encore avancé : ils seront supprimés avec le processus.'}
                        </li>
                    </ul>
                </StageModale>
            )}

            {/* ── Récapitulatif AVANT la reprise N-1 ─────────────────────── */}
            {confirmeN1 && (
                <StageModale
                    titre="Reprendre les processus de l'année précédente ?"
                    icone="fa-rotate-left"
                    enCours={enCours}
                    libelleValider="Reprendre"
                    onValider={recupererN1}
                    onFermer={() => setConfirmeN1(false)}
                >
                    <div>Chaque processus de l'année précédente sera recopié sur {donnees.annee?.libelle} :</div>
                    <ul>
                        <li>ses formations, ses documents et courriels et son paramétrage des étapes ;</li>
                        <li><b>ses dates ne sont pas copiées</b> — ce sont précisément celles qui changent ;</li>
                        <li>les dossiers de stage des étudiants concernés seront ouverts ;</li>
                        <li>un processus déjà présent cette année ne sera pas dupliqué.</li>
                    </ul>
                </StageModale>
            )}

            {/* ── Confirmation APRÈS l'acte ──────────────────────────────── */}
            {bilan && (
                <StageModale
                    titre={bilan.titre}
                    icone="fa-circle-check"
                    ton="succes"
                    onFermer={() => { const apres = bilan.apres; setBilan(null); if (apres) apres(); }}
                >
                    {bilan.lignes.map((l, i) => (
                        <div key={i} style={{ marginBottom: 4 }}>{l}</div>
                    ))}
                </StageModale>
            )}
        </>
    );
}

export default StageParametrageConfigurations;
